import { Box, Text, TextButton } from '@zillow/constellation-10';

import { useRentalManagerUserListingConversationWebApiHandler_getConversationCountsForListing } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { ENVELOPE_EMPTY_ILLUSTRATION_URL } from '../../../../constants/DigitalLibraryConstants';

import EmptyState from '../../../../components/EmptyState';
import SpotIllustration from '../../../../components/EmptyState/SpotIllustration';
import SimpleCard from '../../../../components/SimpleCard';

import ConnectedModuleCard from '../../../ConnectedModuleCard';

type Props = {
  listingAlias: string;
  inquiriesPath?: string;
};

const CARD_LABELS = {
  NEW: 'New',
  UNREPLIED: 'Unreplied',
  TOTAL: 'Total',
};

const InquiriesModule = ({ listingAlias, inquiriesPath }: Props) => {
  const { data, isPending, isError } =
    useRentalManagerUserListingConversationWebApiHandler_getConversationCountsForListing(
      {
        listingAlias,
        calculateInRealTime: true,
      },
      { enabled: Boolean(listingAlias), staleTime: 8 * 1000 },
    );

  const { unreadCount = 0, notRepliedToCount = 0, totalConversationsCount = 0 } = data || {};
  const hasConversations = totalConversationsCount > 0;

  const renderCTA = () => {
    if (!inquiriesPath || (!hasConversations && !isPending)) return null;

    return (
      <TextButton tone="brand" asChild>
        <a href={inquiriesPath} target="_self">
          View in messages
        </a>
      </TextButton>
    );
  };

  const getInquiriesHref = (filter: string): string | undefined => {
    if (!inquiriesPath) return undefined;

    const queryParams = filter ? `?${filter}=true&byProperty=true` : '?byProperty=true';
    return `${inquiriesPath}${queryParams}`;
  };

  const renderContent = () => {
    if (isError) {
      return (
        <EmptyState
          icon={<SpotIllustration src={ENVELOPE_EMPTY_ILLUSTRATION_URL} />}
          heading="Unable to load inquiries"
          headingLevel={4}
          body="Refresh the page to try again."
          align="left"
          useCard={false}
        />
      );
    }

    if (!hasConversations && !isPending) {
      return (
        <EmptyState
          data-testid="EmptyInquiriesCardSimple"
          icon={<SpotIllustration src={ENVELOPE_EMPTY_ILLUSTRATION_URL} />}
          heading="No inquiries yet"
          headingLevel={4}
          align="left"
          useCard={false}
          body="When a renter contacts you, help make a good first impression by responding within 24 hours."
        />
      );
    }

    return (
      <Box
        css={{
          display: 'grid',
          gridTemplateColumns: 'repeat(1, minmax(0,1fr))',
          gap: { base: '200', lg: '400' },
        }}
      >
        <Box data-testid="Grid-New">
          <SimpleCard
            withChevron={Boolean(inquiriesPath)}
            href={getInquiriesHref('unreadOnly')}
            linkLabel={CARD_LABELS.NEW}
            isLoading={isPending}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.NEW}</Text>
              {typeof unreadCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {unreadCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>

        <Box data-testid="Grid-Unreplied">
          <SimpleCard
            withChevron={Boolean(inquiriesPath)}
            href={getInquiriesHref('notRepliedToOnly')}
            linkLabel={CARD_LABELS.UNREPLIED}
            isLoading={isPending}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.UNREPLIED}</Text>
              {typeof notRepliedToCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {notRepliedToCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>

        <Box data-testid="Grid-Total">
          <SimpleCard
            withChevron={Boolean(inquiriesPath)}
            href={getInquiriesHref('')}
            linkLabel={CARD_LABELS.TOTAL}
            isLoading={isPending}
          >
            <Box
              css={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Text textStyle="body-sm">{CARD_LABELS.TOTAL}</Text>
              {typeof totalConversationsCount === 'number' && (
                <Text textStyle="body-sm-bold" css={{ marginTop: isPending ? '0' : '200' }}>
                  {totalConversationsCount}
                </Text>
              )}
            </Box>
          </SimpleCard>
        </Box>
      </Box>
    );
  };

  return (
    <div data-testid="InquiriesModule">
      <ConnectedModuleCard
        title="Inquiries"
        ctaComponent={renderCTA()}
        ctaFontType="body-sm"
        justifyHeadingContent="center"
      >
        {renderContent()}
      </ConnectedModuleCard>
    </div>
  );
};

export default InquiriesModule;
